import { useContext, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { MoreVerticalIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useSplitOpsOrderMutation } from '@/graphql/mutations/splitOpsOrder.generated';
import { useUpdateOpsOrderMutation } from '@/graphql/mutations/updateOpsOrder.generated';
import { useWarehousesQuery } from '@/graphql/queries/warehouses.generated';
import { resolveAutomaticWarehouseUuid } from './warehouseRouting';
import { useGetOrderWarehouseConfigQuery } from '@/graphql/queries/getOrderWarehouseConfig.generated';
import { useCreateReplacementSendCloudShipmentMutation } from '@/graphql/mutations/createReplacementSendCloudShipment.generated';
import { useRetrySendCloudLabelMutation } from '@/graphql/mutations/retrySendCloudLabel.generated';
import { useSendToPickingAction } from '../../hooks/useSendToPickingAction';
import { useRefetchOrderDocuments } from '../../hooks/useRefetchOrderDocuments';
import { useRefetchDeliveryNote } from '../../hooks/useRefetchDeliveryNote';
import {
  ViewerContext,
  CanAccessPartnerData,
} from '@/providers/ViewerContextProvider';
import {
  SHIPPING_LABEL_ELIGIBLE_STATUSES,
  SHIPPING_LABEL_VIEWABLE_STATUSES,
} from '../../utils/labelStatuses';
import { toast } from 'sonner';
import { ApolloError } from '@apollo/client';
import i18n from '@/utils/i18n';

import {
  MerchantOrderStatusEnum,
  MerchantOrderTypeEnum,
  RetrySendCloudLabelOutcome,
} from '@/graphql/generatedTypes';
import { canTransitionTo } from '../../utils/orderTransitionRules';
import { CommentField, SectionDivider } from './DetailFormParts';
import {
  CarrierStatusTable,
  PackageItemsTable,
  PackageWeightsTable,
} from './DetailTables';
import type { OpsOrderDetail } from './types';
import { useOrderForm } from './useOrderForm';
import { SaveDiscardBottomBar } from './SaveDiscardBottomBar';
import { AddressForm } from '@/components/forms/address/AddressForm';
import { Routes } from '@/utils/routes';
import { OrderSidebar } from './OrderDetailSidebar';
import { StatusHistorySheet } from './StatusHistorySheet';
import { MarkAsPickedDialog } from './MarkAsPickedDialog';
import { useOrderChangeGuard } from './useOrderChangeGuard';
import { useSendcloudResetGuard } from './useSendcloudResetGuard';
import { ReplacementShipmentDialog } from './ReplacementShipmentDialog';
import { isReturnedToSender } from './orderFormPolicy';
import { pickingJobMenuItem } from './pickingJobLinkPolicy';

interface OrderDetailsFormProps {
  order: OpsOrderDetail;
  refetch: () => Promise<unknown>;
}

function scrollInvalidFieldIntoView(field: string) {
  requestAnimationFrame(() => {
    const fieldElement = document.querySelector<HTMLElement>(
      `[data-field="${field}"]`,
    );
    if (!fieldElement) return;

    fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const focusTarget = fieldElement.matches(
      'input,button,textarea,select,[tabindex]',
    )
      ? fieldElement
      : fieldElement.querySelector<HTMLElement>(
          'input,button,textarea,select,[tabindex]',
        );
    focusTarget?.focus({ preventScroll: true });
  });
}

// colugo puts the error code in the GraphQL error's message.
function isGraphQlErrorCode(error: unknown, code: string): boolean {
  return (
    error instanceof ApolloError &&
    error.graphQLErrors.some((e) => e.message === code)
  );
}

export function OrderDetailsForm({ order, refetch }: OrderDetailsFormProps) {
  const history = useHistory();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [markPickedOpen, setMarkPickedOpen] = useState(false);
  const [replacementOpen, setReplacementOpen] = useState(false);

  const { handleSendToPicking, pickingLoading } = useSendToPickingAction({
    onSuccess: async () => {
      toast.success(i18n.t('Order:toastSendToPickingSuccess'));
      await refetch();
    },
    onPartialError: () => {
      refetch();
    },
  });
  const { handleRefetchOrderDocuments, refetchingOrderDocuments } =
    useRefetchOrderDocuments();
  const { handleRefetchDeliveryNote, refetchingDeliveryNote } =
    useRefetchDeliveryNote();
  const viewer = useContext(ViewerContext);
  const canAccessPartnerData = CanAccessPartnerData(viewer);
  const [splitOpsOrder, { loading: splitting }] = useSplitOpsOrderMutation();
  const [updateOrder, { loading: saving }] = useUpdateOpsOrderMutation();
  const { data: warehousesData } = useWarehousesQuery();
  const { data: warehouseRoutingData } = useGetOrderWarehouseConfigQuery();
  const [createReplacementShipment, { loading: creatingReplacement }] =
    useCreateReplacementSendCloudShipmentMutation();
  const [retrySendCloudLabel, { loading: retryingLabel }] =
    useRetrySendCloudLabelMutation();

  const {
    form,
    errors,
    setAddressField,
    setItemAmount,
    removeItems,
    addItem,
    addUntrackedItem,
    addStoreAssetItem,
    setPackageWeight,
    setComment,
    setOpsComment,
    setStatus,
    setCarrier,
    setLanguage,
    setWarehouseUuid,
    isFieldSupported,
    isFieldRequired,
    isReadOnly,
    canEditAddress,
    canEditItems,
    canEditPackageWeights,
    canEditCarrier,
    canEditWarehouse,
    canEditStatus,
    canEditLanguage,
    isDirty,
    needsChangeGuard,
    resetToInitial,
    orderKind,
    validate,
    buildUpdateInput,
  } = useOrderForm(order);

  const automaticWarehouseUuid = warehouseRoutingData
    ? resolveAutomaticWarehouseUuid(
        warehouseRoutingData.getOrderWarehouseConfig.non_event,
        form.address.country,
      )
    : order.warehouse_is_automatic
      ? order.warehouse_uuid
      : null;

  const { requestConfirmation, OrderChangeGuardDialog } = useOrderChangeGuard(
    form.comment,
  );
  const { requestSendcloudReset, SendcloudResetDialog } =
    useSendcloudResetGuard();

  const showRefetchOrderDocuments =
    orderKind !== 'inbound' &&
    SHIPPING_LABEL_ELIGIBLE_STATUSES.has(order.status);
  // Greyed out while the order is only sent to picking; usable once picked.
  const orderDocumentsViewable = SHIPPING_LABEL_VIEWABLE_STATUSES.has(
    order.status,
  );
  // Escape hatch: when the normal "view order documents" button is not
  // available (e.g. the order was cancelled), partner-data users still get a
  // dedicated button to reach the delivery note (Lieferschein). Gated on the
  // same role set as colugo's `canAccessPartnerData`
  // (utils/MiddleWares/Admin.js) so the two cannot drift apart.
  const showDeliveryNoteAction =
    canAccessPartnerData &&
    orderKind !== 'inbound' &&
    !showRefetchOrderDocuments;

  const isInboundPickup =
    orderKind === 'inbound' &&
    order.system === 'OPEN_SYSTEM' &&
    (order.type === MerchantOrderTypeEnum.Pickup ||
      order.type === MerchantOrderTypeEnum.DpdPickup);

  const showMarkAsPicked =
    isInboundPickup &&
    (order.status === MerchantOrderStatusEnum.OrderReceived ||
      order.status === MerchantOrderStatusEnum.Approved);

  const handleSave = async () => {
    const isInboundOrder = orderKind === 'inbound';
    if (!isInboundOrder) {
      const invalidFields = validate();
      const invalidFieldKeys = Object.keys(invalidFields ?? {});
      if (invalidFieldKeys.length > 0) {
        scrollInvalidFieldIntoView(invalidFieldKeys[0]);
        toast.error(
          i18n.t('MerchantOrder:alertAddressNotComplete', {
            attribute: invalidFieldKeys.join(', '),
          }),
        );
        return;
      }
    }

    let commentOverride: string | undefined;
    if (needsChangeGuard) {
      const result = await requestConfirmation();
      if (!result.confirmed) return;
      commentOverride = result.comment;
    }

    // On ERROR → APPROVED the backend can wipe the order's SendCloud state so
    // a fresh SendCloud order is announced from the corrected data; let the
    // user decide before saving. Ignored on any other transition.
    let resetSendcloud = false;
    if (
      order.status === MerchantOrderStatusEnum.Error &&
      form.status === MerchantOrderStatusEnum.Approved
    ) {
      const result = await requestSendcloudReset();
      if (!result.confirmed) return;
      resetSendcloud = result.reset;
    }

    try {
      const update = buildUpdateInput();
      if (commentOverride !== undefined) {
        update.comment = commentOverride;
      }
      if (resetSendcloud) {
        update.reset_sendcloud = true;
      }
      await updateOrder({ variables: { id: order.id, update } });
      refetch();
      toast.success(i18n.t('Order:toastSaveSuccess'));
    } catch {
      toast.error(i18n.t('Order:toastSaveError'));
    }
  };

  const handleSplitSelectedItems = async (selectedIds: string[]) => {
    if (order.system !== 'OPEN_SYSTEM') return;

    const selected = form.items.filter(
      (item) => selectedIds.includes(item.id) && item.amount > 0,
    );
    const splitContainers = selected
      .filter((item) => item.kind === 'container')
      .map((item) => {
        return { id: item.id, amount: item.amount, _typeId: item.type_id };
      });
    const splitUntrackedItems = selected
      .filter((item) => item.kind === 'untracked')
      .map((item) => {
        return { id: item.id, amount: item.amount, _typeId: item.type_id };
      });
    const splitStoreAssets = selected
      .filter((item) => item.kind === 'store_asset')
      .map((item) => {
        return { id: item.id, amount: item.amount, _typeId: item.type_id };
      });

    if (
      splitContainers.length === 0 &&
      splitUntrackedItems.length === 0 &&
      splitStoreAssets.length === 0
    ) {
      toast.error(i18n.t('Order:toastSelectItemValidation'));
      return;
    }

    const guardResult = await requestConfirmation();
    if (!guardResult.confirmed) return;

    let updatedOrder: OpsOrderDetail | undefined;

    try {
      const updateOrderRes = await updateOrder({
        variables: {
          id: order.id,
          update: { comment: guardResult.comment },
        },
      });
      updatedOrder = updateOrderRes.data?.updateOpsOrder;
    } catch {
      toast.error(i18n.t('Order:toastSaveError'));
      return;
    }

    // At this point, the order has been updated, so the item ids are no longer valid
    // We need to use the updated order to get the new item ids by matching the type id
    const typeIdToContainerIdMap: Record<number, string> = {};
    const typeIdToUntrackedItemIdMap: Record<number, string> = {};
    const itemTypeIdToStoreAssetIdMap: Record<number, string> = {};

    updatedOrder?.containers.forEach((c) => {
      typeIdToContainerIdMap[c.type.id] = c.id;
    });
    updatedOrder?.untracked_items.forEach((ui) => {
      typeIdToUntrackedItemIdMap[ui.type.id] = ui.id;
    });
    updatedOrder?.items.forEach((it) => {
      itemTypeIdToStoreAssetIdMap[it.item_type.id] = it.id;
    });

    const updatedSplitContainers = splitContainers.map((c) => ({
      amount: c.amount,
      id: typeIdToContainerIdMap[c._typeId],
    }));
    const updatedSplitUntrackedItems = splitUntrackedItems.map((u) => ({
      amount: u.amount,
      id: typeIdToUntrackedItemIdMap[u._typeId],
    }));
    const updatedSplitStoreAssets = splitStoreAssets.map((a) => ({
      amount: a.amount,
      id: itemTypeIdToStoreAssetIdMap[a._typeId],
    }));

    try {
      const result = await splitOpsOrder({
        variables: {
          id: order.id,
          containers:
            updatedSplitContainers.length > 0
              ? updatedSplitContainers
              : undefined,
          untracked_items:
            updatedSplitUntrackedItems.length > 0
              ? updatedSplitUntrackedItems
              : undefined,
          items:
            updatedSplitStoreAssets.length > 0
              ? updatedSplitStoreAssets
              : undefined,
        },
      });
      await refetch().catch(() => null);
      const newOrderId = result.data?.splitOpsOrder;
      if (!newOrderId) {
        toast.error(i18n.t('Order:toastSplitNotCreated'));
        return;
      }
      const newOrderUrl = Routes.orderDetails(newOrderId);
      window.open(newOrderUrl, '_blank');
      toast.success(i18n.t('Order:toastSplitSuccess'), {
        action: {
          label: i18n.t('Order:toastSplitSuccessAction'),
          onClick: () => window.open(newOrderUrl, '_blank'),
        },
      });
    } catch {
      toast.error(i18n.t('Order:toastSplitError'));
    }
  };

  const canSplitOrder = order.system === 'OPEN_SYSTEM';

  // The replacement announces whatever address the order carries in the
  // database, so an unsaved edit would ship to the old one. Saving first is the
  // guard, not a nicety.
  const showReplacementAction =
    canAccessPartnerData &&
    orderKind === 'outbound' &&
    isReturnedToSender(order.status);

  // Both terminal outbound failures have stock stranded somewhere ops has to
  // account for: an ERROR whose shipment never arrived, and a RETURN_TO_SENDER
  // ops has decided not to replace. That decision is the picking job's — one
  // job owns one set of picked stock for every order on it — so the order page
  // only points there. Every other linked job still gets a plain link.
  const pickingJobItem = pickingJobMenuItem({
    viewer,
    orderKind,
    status: order.status,
    pickingJob: order.picking_job,
  });

  const handleCreateReplacement = async () => {
    try {
      const result = await createReplacementShipment({
        variables: { order_id: order.id },
      });
      const replacement = result.data?.createReplacementSendCloudShipment;
      setReplacementOpen(false);
      await refetch().catch(() => null);
      if (replacement?.created === false) {
        toast.info(i18n.t('Order:toastReplacementShipmentAlreadyExists'));
      } else {
        toast.success(i18n.t('Order:toastReplacementShipmentSuccess'));
      }
      if (replacement?.labels_url) {
        window.open(replacement.labels_url, '_blank');
      }
    } catch {
      toast.error(i18n.t('Order:toastReplacementShipmentError'));
    }
  };

  const handleRetryLabel = async () => {
    try {
      const result = await retrySendCloudLabel({
        variables: { order_id: order.id },
      });
      const outcome = result.data?.retrySendCloudLabel.outcome;
      if (outcome === RetrySendCloudLabelOutcome.Recovered) {
        toast.success(i18n.t('Order:toastLabelRetryRecovered'));
      } else if (outcome === RetrySendCloudLabelOutcome.Failed) {
        toast.error(i18n.t('Order:toastLabelRetryFailed'));
      } else if (outcome === RetrySendCloudLabelOutcome.InProgress) {
        toast.info(i18n.t('Order:toastLabelRetryInProgress'));
      } else {
        toast.warning(i18n.t('Order:toastLabelRetryStillRetrying'));
      }
    } catch (error) {
      // INVALID_INPUT: the order left ERROR_RETRYABLE since the page loaded,
      // usually because the cron recovered the label. Not a failure.
      if (isGraphQlErrorCode(error, 'INVALID_INPUT')) {
        toast.info(i18n.t('Order:toastLabelRetryNotRetryable'));
      } else {
        toast.error(i18n.t('Order:toastLabelRetryError'));
      }
    }
    // Also after an error: the reloaded status replaces the stale alert.
    await refetch().catch(() => null);
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-4 tw:pb-6">
        <div className="tw:flex tw:flex-col tw:gap-2">
          <h1 className="tw:text-4xl tw:font-extrabold">
            {i18n.t('MerchantOrder:orderDetails')}
          </h1>
          <p>{i18n.t('Order:detailPageDescription')}</p>
        </div>
        <div className="tw:flex tw:items-center tw:gap-2">
          {showReplacementAction && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0}>
                  <Button
                    size="sm"
                    onClick={() => setReplacementOpen(true)}
                    disabled={isDirty || creatingReplacement}
                  >
                    {creatingReplacement && <Spinner />}
                    {i18n.t('Order:replacementShipmentAction')}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="tw:max-w-xs">
                {isDirty
                  ? i18n.t('Order:replacementShipmentSaveAddressFirst')
                  : i18n.t('Order:replacementShipmentTooltip')}
              </TooltipContent>
            </Tooltip>
          )}
          {showMarkAsPicked ? (
            <Button size="sm" onClick={() => setMarkPickedOpen(true)}>
              {i18n.t('Order:markAsPicked')}
            </Button>
          ) : showRefetchOrderDocuments ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => handleRefetchOrderDocuments([order.id])}
              disabled={refetchingOrderDocuments || !orderDocumentsViewable}
            >
              {refetchingOrderDocuments && <Spinner />}
              {i18n.t('Order:viewOrderDocuments')}
            </Button>
          ) : (
            (() => {
              const missingStoreUser = !order.target_store_user_id;
              const button = (
                <Button
                  size="sm"
                  onClick={() => handleSendToPicking([order.id])}
                  disabled={
                    orderKind === 'inbound' ||
                    !canTransitionTo(
                      order.system,
                      order.status,
                      MerchantOrderStatusEnum.SentToPicking,
                    ) ||
                    pickingLoading ||
                    missingStoreUser
                  }
                >
                  {pickingLoading && <Spinner />}
                  {i18n.t('Order:sendToPicking')}
                </Button>
              );
              if (missingStoreUser) {
                return (
                  <Tooltip>
                    {/* span wrapper so the tooltip can catch hover on a disabled button */}
                    <TooltipTrigger asChild>
                      <span tabIndex={0}>{button}</span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="tw:max-w-xs">
                      {i18n.t('Order:sendToPickingNoStoreUserTooltip')}
                    </TooltipContent>
                  </Tooltip>
                );
              }
              return button;
            })()
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="icon">
                <MoreVerticalIcon />
                <span className="tw:sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {showDeliveryNoteAction && (
                <DropdownMenuItem
                  disabled={refetchingDeliveryNote}
                  onClick={() => handleRefetchDeliveryNote([order.id])}
                >
                  {i18n.t('Order:downloadDeliveryNote')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                disabled={!order.store_text_id}
                onClick={() =>
                  order.store_text_id &&
                  history.push(Routes.pickpackStores(order.store_text_id))
                }
              >
                {i18n.t('Order:goToStore')}
              </DropdownMenuItem>
              {pickingJobItem && (
                <DropdownMenuItem asChild>
                  <Link to={Routes.pickingJobDetails(pickingJobItem.jobId)}>
                    {pickingJobItem.action === 'resolve'
                      ? i18n.t('Order:resolveOnPickingJob')
                      : i18n.t('Order:openPickingJob')}
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setHistoryOpen(true)}>
                {i18n.t('Order:viewHistory')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <CarrierStatusTable rows={order.carrier_status} orderId={order.id} />

      <div className="tw:flex tw:items-start tw:gap-4">
        <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-4">
          {orderKind === 'outbound' && (
            <Card>
              <CardHeader>
                <CardTitle>{i18n.t('Order:sectionPartnerComment')}</CardTitle>
              </CardHeader>
              <CardContent>
                <CommentField
                  value={form.comment}
                  onChange={setComment}
                  disabled={isReadOnly}
                />
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>{i18n.t('Order:noteToOps')}</CardTitle>
            </CardHeader>
            <CardContent>
              <CommentField
                value={form.opsComment}
                onChange={setOpsComment}
                placeholder={i18n.t('Order:opsCommentPlaceholder')}
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="tw:flex tw:flex-col tw:gap-8">
              {orderKind === 'outbound' && (
                <>
                  <PackageItemsTable
                    items={form.items}
                    orderId={order.id}
                    storeTextId={order.store_text_id}
                    orderType={order.type}
                    orderKind={orderKind}
                    onAmountChange={setItemAmount}
                    onAddItem={canEditItems ? addItem : undefined}
                    onAddUntrackedItem={
                      canEditItems ? addUntrackedItem : undefined
                    }
                    onAddStoreAssetItem={
                      canEditItems ? addStoreAssetItem : undefined
                    }
                    disabled={!canEditItems}
                    splitLoading={splitting}
                    onSplitSelected={
                      canSplitOrder ? handleSplitSelectedItems : undefined
                    }
                    onDeleteSelected={canEditItems ? removeItems : undefined}
                  />

                  <SectionDivider
                    text={i18n.t('Order:sectionPackageWeights')}
                  />
                  <PackageWeightsTable
                    weights={form.packageWeights}
                    onWeightChange={setPackageWeight}
                    disabled={!canEditPackageWeights}
                    errors={errors}
                  />
                </>
              )}

              {orderKind === 'outbound' && (
                <>
                  <SectionDivider
                    text={i18n.t('MerchantOrder:deliveryAddress')}
                  />
                  <AddressForm
                    value={form.address}
                    errors={errors}
                    disabled={!canEditAddress}
                    isFieldSupported={isFieldSupported}
                    isFieldRequired={isFieldRequired}
                    onFieldChange={setAddressField}
                  />
                </>
              )}
              {orderKind === 'inbound' && (
                <>
                  <SectionDivider text={i18n.t('Order:sectionSenderAddress')} />
                  <AddressForm value={form.address} disabled />
                </>
              )}
            </CardContent>
          </Card>
        </div>

        <OrderSidebar
          orderKind={orderKind}
          status={form.status}
          onStatusChange={setStatus}
          system={order.system}
          warehouse={order.warehouse}
          warehouseUuid={form.warehouseUuid}
          automaticWarehouseUuid={automaticWarehouseUuid}
          warehouses={warehousesData?.warehouses}
          onWarehouseChange={setWarehouseUuid}
          warehouseDisabled={!canEditWarehouse}
          carrier={form.carrier}
          onCarrierChange={setCarrier}
          carrierService={form.carrierService}
          availableCarrierServices={order.available_carrier_services}
          carrierPickupDate={order.carrier_pickup_date}
          language={form.language}
          onLanguageChange={setLanguage}
          orderType={order.type!}
          packageCount={order.package_count}
          statusDisabled={showMarkAsPicked || !canEditStatus}
          fieldsDisabled={!canEditLanguage}
          carrierDisabled={!canEditCarrier}
          errorType={order.error_type}
          errorMessage={order.error_message}
          lastUpdated={order.last_updated}
          pickingJob={order.picking_job}
          onRetryLabel={canAccessPartnerData ? handleRetryLabel : undefined}
          retryingLabel={retryingLabel}
        />
      </div>

      <SaveDiscardBottomBar
        visible={isDirty}
        saving={saving}
        onSave={handleSave}
        onDiscard={resetToInitial}
      />

      <StatusHistorySheet
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        statusHistory={order.status_history}
      />

      <MarkAsPickedDialog
        open={markPickedOpen}
        onOpenChange={setMarkPickedOpen}
        orderId={order.id}
        onSuccess={refetch}
      />

      <ReplacementShipmentDialog
        open={replacementOpen}
        loading={creatingReplacement}
        onOpenChange={setReplacementOpen}
        onConfirm={handleCreateReplacement}
      />

      {OrderChangeGuardDialog}
      {SendcloudResetDialog}
    </div>
  );
}
